import bottleBotService from '@/services/bottleBot.service'

import { createBottleBotState } from './bottleBot.state'

export const TYPING_TIMEOUT_MS = 45000
export const NOTICE_POLL_MS = 30000
export const NOTICE_POLL_MAX = 6

export const delayNoticeMessage = conversationId => ({
  id: `local-delay-notice-${conversationId}`,
  role: 'assistant',
  local_notice: true,
  conversation_id: conversationId,
  content:
    "This one's taking longer than usual. I'm still working on it — the answer will appear here as soon as it's ready.",
  render_blocks: []
})

export function createBotModule({ platform }) {
  let tempSeq = 0
  let typingTimer = null
  let sendGeneration = 0

  const clearTypingTimer = () => {
    if (typingTimer) {
      clearTimeout(typingTimer)
      typingTimer = null
    }
  }

  const conversationParams = platform === 'catering' ? {} : { platform }

  return {
    namespaced: true,

    state: createBottleBotState,

    getters: {
      activeConversation: state => state.conversations.find(c => c.id === state.activeConversationId) || null,
      sortedConversations: state =>
        [...state.conversations].sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0))
    },

    mutations: {
      SET_MODE: (state, mode) => (state.mode = mode),
      SET_VIEW: (state, view) => (state.view = view),
      SET_TYPING: (state, typing) => (state.typing = typing),
      SET_SENDING: (state, sending) => (state.sending = sending),
      SET_CONVERSATIONS: (state, { conversations, next_cursor }) => {
        state.conversations = conversations
        state.conversationsCursor = next_cursor
        state.conversationsLoaded = true
      },
      APPEND_CONVERSATIONS: (state, { conversations, next_cursor }) => {
        state.conversations = [...state.conversations, ...conversations]
        state.conversationsCursor = next_cursor
      },
      UPSERT_CONVERSATION: (state, conversation) => {
        const idx = state.conversations.findIndex(c => c.id === conversation.id)
        if (idx === -1) state.conversations = [conversation, ...state.conversations]
        else state.conversations.splice(idx, 1, { ...state.conversations[idx], ...conversation })
      },
      REMOVE_CONVERSATION: (state, id) => {
        state.conversations = state.conversations.filter(c => c.id !== id)
        if (state.activeConversationId === id) {
          state.activeConversationId = null
          state.messages = []
          state.messagesCursor = null
        }
      },
      SET_ACTIVE_CONVERSATION: (state, id) => (state.activeConversationId = id),
      SET_MESSAGES: (state, { messages, next_cursor }) => {
        state.messages = messages
        state.messagesCursor = next_cursor
      },
      PREPEND_MESSAGES: (state, { messages, next_cursor }) => {
        state.messages = [...messages, ...state.messages]
        state.messagesCursor = next_cursor
      },
      APPEND_MESSAGE: (state, message) => {
        if (state.messages.some(m => m.id === message.id)) return
        state.messages = [...state.messages, message]
      },
      REPLACE_MESSAGE: (state, { tempId, message }) => {
        const idx = state.messages.findIndex(m => m.id === tempId)
        if (idx === -1) {
          if (!state.messages.some(m => m.id === message.id)) state.messages = [...state.messages, message]
        } else {
          state.messages.splice(idx, 1, message)
        }
      },
      MARK_MESSAGE_FAILED: (state, id) => {
        const idx = state.messages.findIndex(m => m.id === id)
        if (idx !== -1) state.messages.splice(idx, 1, { ...state.messages[idx], pending: false, failed: true })
      },
      REMOVE_LOCAL_NOTICES: state => {
        state.messages = state.messages.filter(m => !m.local_notice)
      }
    },

    actions: {
      async fetchConversations({ commit, state }, { loadMore = false } = {}) {
        const cursor = loadMore ? state.conversationsCursor : null
        const data = await bottleBotService.listConversations({ cursor, ...conversationParams })
        commit(loadMore ? 'APPEND_CONVERSATIONS' : 'SET_CONVERSATIONS', data)
      },

      async openConversation({ commit }, conversationId) {
        sendGeneration += 1
        clearTypingTimer()
        commit('SET_ACTIVE_CONVERSATION', conversationId)
        commit('SET_TYPING', false)
        const data = await bottleBotService.listMessages(conversationId, { cursor: null })
        commit('SET_MESSAGES', data)
      },

      async loadMoreMessages({ commit, state }) {
        if (!state.activeConversationId || !state.messagesCursor) return
        const data = await bottleBotService.listMessages(state.activeConversationId, { cursor: state.messagesCursor })
        commit('PREPEND_MESSAGES', data)
      },

      async refreshActiveConversation({ commit, state }) {
        const conversationId = state.activeConversationId
        if (!conversationId) return
        if (state.sending || state.messages.some(m => m.pending)) return
        const knownIds = new Set(state.messages.map(m => m.id))
        const hadNotice = state.messages.some(m => m.local_notice)
        let data
        try {
          data = await bottleBotService.listMessages(conversationId, { cursor: null })
        } catch (error) {
          return
        }
        if (conversationId !== state.activeConversationId) return
        const landed = data.messages.some(m => m.role === 'assistant' && !knownIds.has(m.id))
        if (landed) {
          clearTypingTimer()
          commit('SET_TYPING', false)
        }
        commit('SET_MESSAGES', data)
        if (!landed && hadNotice) commit('APPEND_MESSAGE', delayNoticeMessage(conversationId))
      },

      async startNewConversation({ commit }) {
        sendGeneration += 1
        clearTypingTimer()
        commit('SET_TYPING', false)
        const { conversation } = await bottleBotService.createConversation(conversationParams)
        commit('UPSERT_CONVERSATION', conversation)
        commit('SET_ACTIVE_CONVERSATION', conversation.id)
        commit('SET_MESSAGES', { messages: [], next_cursor: null })
        commit('SET_VIEW', 'chat')
      },

      async sendMessage({ commit, state, dispatch }, content) {
        if (!state.activeConversationId) await dispatch('startNewConversation')
        const conversationId = state.activeConversationId
        const tempId = `temp-${++tempSeq}`
        commit('APPEND_MESSAGE', {
          id: tempId,
          role: 'user',
          content,
          conversation_id: conversationId,
          render_blocks: [],
          pending: true
        })
        commit('SET_TYPING', true)
        clearTypingTimer()
        const generation = ++sendGeneration
        typingTimer = setTimeout(() => {
          typingTimer = null
          dispatch('replyTimedOut', { conversationId, generation })
        }, TYPING_TIMEOUT_MS)
        commit('SET_SENDING', true)
        try {
          const { message, conversation } = await bottleBotService.createMessage(conversationId, content)
          commit('REPLACE_MESSAGE', { tempId, message })
          commit('UPSERT_CONVERSATION', conversation)
        } catch (error) {
          commit('MARK_MESSAGE_FAILED', tempId)
          clearTypingTimer()
          commit('SET_TYPING', false)
          commit('REMOVE_LOCAL_NOTICES')
          throw error
        } finally {
          commit('SET_SENDING', false)
        }
      },

      async renameConversation({ commit }, { id, title }) {
        const { conversation } = await bottleBotService.renameConversation(id, title)
        commit('UPSERT_CONVERSATION', conversation)
      },

      async deleteConversation({ commit }, id) {
        await bottleBotService.deleteConversation(id)
        commit('REMOVE_CONVERSATION', id)
      },

      async replyTimedOut({ commit, state, dispatch }, { conversationId, generation, attempt = 1 }) {
        const stale = () => generation !== sendGeneration || conversationId !== state.activeConversationId
        if (stale()) return
        const knownIds = new Set(state.messages.map(m => m.id))
        let data = null
        try {
          data = await bottleBotService.listMessages(conversationId, { cursor: null })
        } catch (error) {
          data = null
        }
        if (stale()) return
        if (state.messages.some(m => m.role === 'assistant' && !knownIds.has(m.id))) return
        if (data && data.messages.some(m => m.role === 'assistant' && !knownIds.has(m.id))) {
          clearTypingTimer()
          commit('SET_TYPING', false)
          commit('SET_MESSAGES', data)
          return
        }
        commit('SET_TYPING', false)
        commit('APPEND_MESSAGE', delayNoticeMessage(conversationId))
        if (attempt < NOTICE_POLL_MAX) {
          typingTimer = setTimeout(() => {
            typingTimer = null
            dispatch('replyTimedOut', { conversationId, generation, attempt: attempt + 1 })
          }, NOTICE_POLL_MS)
        }
      },

      receivePushedMessage({ commit, state }, { conversation_id, message }) {
        commit('UPSERT_CONVERSATION', { id: conversation_id, last_message_at: message.created_at })
        if (conversation_id !== state.activeConversationId) return
        commit('REMOVE_LOCAL_NOTICES')
        commit('APPEND_MESSAGE', message)
        const awaitingNewer = state.messages.some(
          m =>
            m.role === 'user' &&
            (m.pending || (m.sequence !== undefined && message.sequence !== undefined && m.sequence > message.sequence))
        )
        if (!awaitingNewer) {
          clearTypingTimer()
          commit('SET_TYPING', false)
        }
      }
    }
  }
}

const bottleBot = createBotModule({ platform: 'catering' })

export default bottleBot
